import { type RefObject, useCallback } from 'react';
import type { Measurable } from '@radix-ui/rect';

import { VideoSettingsPopover } from '@/components/shared/video-settings-popover';
import { useBlockSettingsState } from '@/hooks/use-block-settings-state';
import { type GroupChildFloat, getBlockFloat } from '@/lib/blocks/layout/group-layout';
import { scheduleScrollToPageEditorBlock } from '@/lib/page-editor/scroll-to-block';
import { readAssetId, readFolderId, readVideoValue } from '@/lib/prop-utils';
import { usePageEditorStore } from '@/stores/page-editor-store';
import { getBlockUiKey, type PageBlock } from '@/types/block';
import type { VideoValue } from '@/types/video';

interface VideoBlockSettingsModalProps {
  sectionId: string;
  block: PageBlock;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorRef?: RefObject<Measurable | null>;
  showFloatOption?: boolean;
}

export function VideoBlockSettingsModal({
  sectionId,
  block,
  open,
  onOpenChange,
  anchorRef,
  showFloatOption = false,
}: VideoBlockSettingsModalProps) {
  const updateBlock = usePageEditorStore((state) => state.updateBlock);
  const previewBlockPropsWithFloatLayout = usePageEditorStore(
    (state) => state.previewBlockPropsWithFloatLayout,
  );
  const cancelBlockPropsWithFloatLayoutPreview = usePageEditorStore(
    (state) => state.cancelBlockPropsWithFloatLayoutPreview,
  );

  const applyChange = useCallback(
    (value: Record<string, unknown> | null, commit: boolean) => {
      const props = value ?? {};
      if (commit) {
        updateBlock(sectionId, block.id, { props });
        return;
      }
      previewBlockPropsWithFloatLayout(sectionId, block.id, props);
    },
    [sectionId, block.id, previewBlockPropsWithFloatLayout, updateBlock],
  );
  const cancelPreview = useCallback(
    () => cancelBlockPropsWithFloatLayoutPreview(sectionId, block.id),
    [block.id, cancelBlockPropsWithFloatLayoutPreview, sectionId],
  );

  const { persistMedia, previewMediaDelete, applyPreview, handleSave, handleCancel } =
    useBlockSettingsState({
      open,
      currentValue: block.props || {},
      mediaKey: 'video',
      applyChange,
      cancelPreview,
    });

  const blockProps = block.props || {};
  const currentVideo = readVideoValue(blockProps.video);
  const currentAssetId = readAssetId(blockProps.video) || null;
  const currentFolderId = readFolderId(blockProps.video) || null;
  const currentAutoplay = !!blockProps.autoplay;
  const currentLoop = !!blockProps.loop;
  const currentFloat = getBlockFloat(block);
  const blockUiKey = getBlockUiKey(block);

  const handleVideoChange = useCallback(
    (video: VideoValue | null) => {
      if (video === null) {
        previewMediaDelete();
      } else {
        persistMedia({ video });
      }
    },
    [persistMedia, previewMediaDelete],
  );

  const handleDescriptionChange = useCallback(
    (description: string) => {
      const vid = readVideoValue(block.props?.video);
      if (!vid) return;
      applyPreview({ video: { ...vid, description } });
    },
    [applyPreview, block.props?.video],
  );

  const handleAutoplayChange = useCallback(
    (autoplay: boolean) => applyPreview({ autoplay }),
    [applyPreview],
  );

  const handleLoopChange = useCallback((loop: boolean) => applyPreview({ loop }), [applyPreview]);

  const handleFloatChange = useCallback(
    (float: GroupChildFloat | null) => {
      if (float === currentFloat) return;
      applyPreview({ float });
      scheduleScrollToPageEditorBlock(blockUiKey);
    },
    [applyPreview, blockUiKey, currentFloat],
  );

  return (
    <VideoSettingsPopover
      open={open}
      onOpenChange={onOpenChange}
      video={currentVideo}
      autoplay={currentAutoplay}
      loop={currentLoop}
      selectedAssetId={currentAssetId}
      selectedFolderId={currentFolderId}
      anchorRef={anchorRef}
      onVideoChange={handleVideoChange}
      onDescriptionChange={handleDescriptionChange}
      onAutoplayChange={handleAutoplayChange}
      onLoopChange={handleLoopChange}
      float={currentFloat}
      onFloatChange={showFloatOption ? handleFloatChange : undefined}
      onSave={handleSave}
      onCancel={handleCancel}
    />
  );
}
